import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { ASSET } from "../../lib/api";
import VendorProductCard from "../../components/VendorProductCard";
import { Store, MapPin, ArrowLeft, Package, Boxes } from "lucide-react";

/**
 * Public storefront (vetrina) for a single vendor / azienda.
 * Route: /negozio/:slug
 */
export default function VendorStore() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [filter, setFilter] = useState("all"); // all | b2c | b2b

  useEffect(() => {
    setLoading(true);
    api
      .get(`/vendor/store/${slug}`)
      .then((r) => {
        setStore(r.data.store);
        setProducts(r.data.products || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const filtered = products.filter((p) =>
    filter === "all" ? true : p.channel === filter
  );
  const countB2c = products.filter((p) => p.channel === "b2c").length;
  const countB2b = products.filter((p) => p.channel === "b2b").length;

  if (loading) {
    return (
      <div className="container-xl py-24 text-center text-muted-foreground">Caricamento vetrina…</div>
    );
  }

  if (notFound || !store) {
    return (
      <div className="container-xl py-24 text-center">
        <Store className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="editorial-heading text-3xl mb-2">Vetrina non trovata</h1>
        <p className="text-muted-foreground mb-6">Il negozio che cerchi non esiste o è stato rimosso.</p>
        <Link to="/negozi" className="text-[hsl(var(--primary))] hover:underline">← Tutte le vetrine</Link>
      </div>
    );
  }

  return (
    <div data-testid="vendor-store-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0a0a0a] to-[#1a0608] text-white">
        <div className="container-xl py-14">
          <Link
            to="/negozi"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C9A961] mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Tutte le vetrine
          </Link>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="w-24 h-24 rounded-sm bg-white/5 border border-[#C9A961]/30 flex items-center justify-center overflow-hidden shrink-0">
              {store.logo_url ? (
                <img src={ASSET(store.logo_url)} alt={store.store_name} className="w-full h-full object-contain" />
              ) : (
                <Store className="h-10 w-10 text-[#C9A961]" />
              )}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-1">Vetrina Fornitore</div>
              <h1 className="editorial-heading text-4xl md:text-5xl">{store.store_name}</h1>
              <div className="mt-2 flex items-center gap-4 text-sm text-white/60 flex-wrap">
                {store.category && <span className="chip bg-white/10 text-white">{store.category}</span>}
                {(store.city || store.country) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {[store.city, store.country].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
            </div>
          </div>
          {store.store_description && (
            <p className="mt-6 max-w-3xl text-white/70 leading-relaxed">{store.store_description}</p>
          )}
        </div>
      </div>

      {/* Products */}
      <div className="container-xl py-12">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <h2 className="editorial-heading text-2xl">Prodotti ({products.length})</h2>
          <div className="flex gap-2">
            {[
              { k: "all", label: `Tutti (${products.length})`, icon: null },
              { k: "b2c", label: `A pezzi (${countB2c})`, icon: Package },
              { k: "b2b", label: `A blocchi (${countB2b})`, icon: Boxes },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`store-filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                  filter === f.k
                    ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                    : "border-border text-muted-foreground hover:border-[hsl(var(--primary))]"
                }`}
              >
                {f.icon && <f.icon className="h-3.5 w-3.5" />}
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">Nessun prodotto in questa categoria.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <VendorProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
